import React from 'react'
import {Link} from 'react-router-dom'
import {ChatAccessDeniedReason} from '@/shared/utils/crud-helper/models'
import {CatalogModule} from '@/app/modules/purchases/catalog'
import {QuotePurchaseKind} from '@/app/modules/purchases/QuoteRequestDialog'
import {SubscriptionQuoteButton} from '@/app/modules/purchases/SubscriptionQuoteButton'
import './styles/ai-chat.scss'

interface AIChatPaywallProps {
  width?: number
  reason: ChatAccessDeniedReason
  onClose?: () => void
}

/** Where the paywall's button leads: a page/site, or the quote dialog for this subscription. */
type PaywallCta =
  | {type: 'link'; label: string; to: string; external?: boolean}
  | {type: 'quote'; label: string; kind: QuotePurchaseKind; initialModules?: CatalogModule[]}

const REASON_CONFIG: Record<
  ChatAccessDeniedReason,
  {icon: string; title: string; message: string; cta: PaywallCta}
> = {
  'no-subscription': {
    icon: 'fas fa-link',
    title: 'No Subscription Linked',
    message:
      'Link your OmicsBox subscription to access AI Chat. Go to Settings to enter your subscription key.',
    cta: {type: 'link', label: 'Go to Settings', to: '/admin'},
  },
  'wrong-product': {
    icon: 'fas fa-box',
    title: 'OmicsBox Subscription Required',
    message:
      'AI Chat is available exclusively with an OmicsBox subscription. Upgrade your plan to get access.',
    cta: {type: 'link', label: 'Get OmicsBox', to: 'https://www.biobam.com/omicsbox/', external: true},
  },
  'not-enabled': {
    icon: 'fas fa-circle-pause',
    title: 'Subscription Inactive',
    message:
      'Your subscription is currently inactive. Please contact support or renew your subscription to use AI Chat.',
    cta: {type: 'link', label: 'Manage Account', to: 'https://account.biobam.com', external: true},
  },
  expired: {
    icon: 'fas fa-calendar-xmark',
    title: 'Subscription Expired',
    message:
      'Your OmicsBox subscription has expired. Renew it to continue using AI Chat and other cloud features.',
    cta: {type: 'quote', label: 'Renew Subscription', kind: 'renew'},
  },
  'no-engine': {
    icon: 'fas fa-robot',
    title: 'Engine Module Required',
    message:
      'The OmicsAI Agent requires the Engine module on your OmicsBox subscription. Add it to your plan to get access.',
    cta: {type: 'quote', label: 'Add Modules', kind: 'add_module', initialModules: ['ENGINE']},
  },
}

const CTA_CLASS = 'btn btn-primary btn-sm'

const PaywallCtaButton: React.FC<{cta: PaywallCta}> = ({cta}) => {
  if (cta.type === 'quote') {
    return (
      <SubscriptionQuoteButton
        kind={cta.kind}
        label={cta.label}
        className={CTA_CLASS}
        initialModules={cta.initialModules}
      />
    )
  }
  if (cta.external) {
    return (
      <a href={cta.to} target='_blank' rel='noopener noreferrer' className={CTA_CLASS}>
        {cta.label}
      </a>
    )
  }
  return (
    <Link to={cta.to} className={CTA_CLASS}>
      {cta.label}
    </Link>
  )
}

export const AIChatPaywall: React.FC<AIChatPaywallProps> = ({
  width = 400,
  reason,
  onClose,
}) => {
  const config = REASON_CONFIG[reason]

  return (
    <div
      className='ai-chat-sidebar ai-chat-sidebar--fixed'
      style={{width: `${width}px`}}
    >
      <div className='ai-chat-sidebar__header'>
        <div className='ai-chat-sidebar__header-left'>
          <span className='ai-chat-sidebar__title-text'>OmicsAI Agent <span className='ai-chat-sidebar__beta-badge'>(beta)</span></span>
        </div>
        <button className='ai-chat-sidebar__close-btn d-lg-none' onClick={onClose} title='Close'>
          <i className='fas fa-xmark'></i>
        </button>
      </div>

      <div className='ai-chat-sidebar__content'>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            padding: '2rem',
            textAlign: 'center',
            gap: '1.25rem',
          }}
        >
          <i
            className={config.icon}
            style={{fontSize: '3rem', color: 'var(--bs-warning)', opacity: 0.85}}
          />
          <h5 style={{margin: 0, fontWeight: 600}}>{config.title}</h5>
          <p style={{margin: 0, color: 'var(--bs-gray-600)', maxWidth: '320px', lineHeight: 1.5}}>
            {config.message}
          </p>
          <PaywallCtaButton cta={config.cta} />
        </div>
      </div>
    </div>
  )
}
